import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';

// Auth providers
import { OrgAuthProvider }       from './contexts/OrgAuthContext';
import { SuperAdminAuthProvider } from './contexts/SuperAdminAuthContext';
import { MemberAuthProvider }    from './contexts/MemberAuthContext';
import { VolunteerAuthProvider } from './contexts/VolunteerAuthContext';

// Protected route guards
import OrgProtectedRoute       from './components/OrgProtectedRoute';
import SuperAdminProtectedRoute from './components/SuperAdminProtectedRoute';
import MemberProtectedRoute    from './components/MemberProtectedRoute';
import VolunteerProtectedRoute from './components/VolunteerProtectedRoute';

// Utility
import DynamicPageTitle from './components/DynamicPageTitle';

// ── Admin Portal ─────────────────────────────────────────────────────────────
import OrgLogin      from './pages/OrgLogin';
import AdminPortal   from './pages/AdminPortal';

// ── SuperAdmin Portal ────────────────────────────────────────────────────────
import SuperAdminLogin     from './pages/SuperAdminLogin';
import SuperAdminDashboard from './pages/SuperAdminDashboard';
import ClientConfigurationPage from './pages/ClientConfigurationPage';

// ── Member Portal ────────────────────────────────────────────────────────────
import MemberLogin  from './pages/MemberLogin';
import MemberPortal from './pages/MemberPortal';

// ── Volunteer Portal ─────────────────────────────────────────────────────────
import VolunteerLogin  from './pages/VolunteerLogin';
import VolunteerPortal from './pages/VolunteerPortal';

// ── Public (no-login) event registration ────────────────────────────────────
import EventRegistrationPage    from './pages/EventRegistrationPage';
import RegistrationResponsePage from './pages/RegistrationResponsePage';


// ── Styles ───────────────────────────────────────────────────────────────────
import './styles/theme.css';
import './styles/admin.css';
import './styles/auth.css';
import './styles/member.css';
import './styles/volunteer.css';

function App() {
  return (
    <Router>
      <DynamicPageTitle />
      <SuperAdminAuthProvider>
        <OrgAuthProvider>
          <MemberAuthProvider>
            <VolunteerAuthProvider>
              <Routes>

                {/* ── SuperAdmin ───────────────────────────────────────── */}
                <Route path="/superadmin/login" element={<SuperAdminLogin />} />                <Route path="/superadmin" element={<SuperAdminProtectedRoute />}>
                  <Route path="dashboard" element={<SuperAdminDashboard />} />
                  <Route path="client-config/:clientId" element={<ClientConfigurationPage />} />
                  <Route index element={<Navigate to="dashboard" replace />} />
                </Route>

                {/* ── Org Admin Portal ─────────────────────────────────── */}
                <Route path="/BespokeBridge/:orgId/admin/login" element={<OrgLogin />} />
                <Route path="/BespokeBridge/:orgId/admin" element={<OrgProtectedRoute />}>
                  <Route path="home"           element={<AdminPortal moduleHint="core" />} />
                  <Route path="core"           element={<AdminPortal moduleHint="core" />} />
                  <Route path="programmes"     element={<AdminPortal moduleHint="programmes" />} />
                  <Route path="community"      element={<AdminPortal moduleHint="community" />} />
                  <Route path="supportmgmt"    element={<AdminPortal moduleHint="supportmgmt" />} />
                  <Route path="communications" element={<AdminPortal moduleHint="communications" />} />
                  <Route path="events"         element={<AdminPortal moduleHint="events" />} />
                  <Route path="reports"        element={<AdminPortal moduleHint="reports" />} />
                  <Route path="ai"             element={<AdminPortal moduleHint="ai" />} />
                  <Route index element={<Navigate to="home" replace />} />
                </Route>

                {/* ── Member Portal ─────────────────────────────────────── */}
                <Route path="/BespokeBridge/:orgId/member/login" element={<MemberLogin />} />
                <Route path="/BespokeBridge/:orgId/member" element={<MemberProtectedRoute />}>
                  <Route path="home"       element={<MemberPortal toolHint="home" />} />
                  <Route path="schemes"    element={<MemberPortal toolHint="schemes" />} />
                  <Route path="programmes" element={<MemberPortal toolHint="programmes" />} />
                  <Route path="jobs"       element={<MemberPortal toolHint="jobs" />} />
                  <Route path="support"    element={<MemberPortal toolHint="support" />} />
                  <Route path="events"     element={<MemberPortal toolHint="events" />} />
                  <Route path="profile"    element={<MemberPortal toolHint="profile" />} />
                  <Route path="directory"  element={<MemberPortal toolHint="directory" />} />
                  <Route index element={<Navigate to="home" replace />} />
                </Route>

                {/* ── Volunteer Portal ──────────────────────────────────── */}
                <Route path="/BespokeBridge/:orgId/volunteer/login" element={<VolunteerLogin />} />
                <Route path="/BespokeBridge/:orgId/volunteer" element={<VolunteerProtectedRoute />}>
                  <Route path="home"       element={<VolunteerPortal toolHint="home" />} />
                  <Route path="schemes"    element={<VolunteerPortal toolHint="programmes" />} />
                  <Route path="programmes" element={<VolunteerPortal toolHint="programmes" />} />
                  <Route path="jobs"       element={<VolunteerPortal toolHint="jobs" />} />
                  <Route path="support"    element={<VolunteerPortal toolHint="support" />} />
                  <Route path="profile"    element={<VolunteerPortal toolHint="profile" />} />
                  <Route path="events"     element={<VolunteerPortal toolHint="events" />} />
                  <Route path="shifts"     element={<VolunteerPortal toolHint="shifts" />} />
                  <Route index element={<Navigate to="home" replace />} />
                </Route>

                {/* ── Public event registration (no login) ────────────── */}
                <Route path="/BespokeBridge/:orgId/register/:eventId" element={<EventRegistrationPage />} />
                <Route path="/BespokeBridge/:orgId/registration/respond" element={<RegistrationResponsePage />} />

                {/* ── Default ───────────────────────────────────────────── */}
                <Route path="/" element={<Navigate to="/superadmin/login" replace />} />
                <Route path="*" element={<Navigate to="/superadmin/login" replace />} />

              </Routes>
            </VolunteerAuthProvider>
          </MemberAuthProvider>
        </OrgAuthProvider>
      </SuperAdminAuthProvider>
    </Router>
  );
}

export default App;
